import styled, { css, DefaultTheme, useTheme } from "styled-components/native";
import {
  KeyboardTypeOptions,
  TextInputProps,
  TouchableOpacity,
  View,
} from "react-native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import React, { useCallback, useEffect, useState } from "react";
import Spacer from "@components/atoms/Spacer";
import VDBText from "@components/atoms/Text";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Platform } from "react-native";
import {
  ButtonCategory,
  ButtonType,
  SelectedItemTheme,
} from "@components/atoms/Button/types";
import { DefaultThemeValueType } from "../../../../styled";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { VDBIcons } from "@utils/VDBIcons";
import KeyboardDoneButton from "./KeyboardDoneButton";
import Button from "../Button";

type IndexProps = TextInputProps & {
  inputRef?: any;
  leftIcon?: string;
  rightIcon?: string;
  rightIconColor?: string;
  hint?: string;
  error?: string;
  inputLabel: string;
  hideIconContainer?: boolean;
  disabled?: boolean;
  isHideBorder?: boolean;
  isShownTopPadding?: boolean;
  onLeftIconPress?: any;
  onRightIconPress?: () => void;
  height?: string;
  currency?: boolean;
  rightButtonIcon?: string;
  rightButtonIconColor?: string;
  rightButtonDisabled?: boolean;
  searchPlaceholder?: string;
  searchTextEntry?: boolean;
  isMultilineTopPadding?: boolean;
  rightButtonIconOnPress?: () => void;
  selectedStyle?: SelectedItemTheme;
  showCurrency?: boolean;
  ignoreDisabledStyle?: boolean;
  leftIconColor?: DefaultThemeValueType;
  disableLeftIcon?: boolean;
  inputType?: KeyboardTypeOptions;
  renderSpaceForErrorMessage?: boolean;
  barcodeButton?: boolean;
  fontFamily?: string;
  rightTextButton?: boolean;
  buttonText?: string;
  rightTextButtonOnPress?: () => void;
  hideErrorIcon?: boolean;
};

const TAPPABLE_AREA = 44;

const Input = (props: IndexProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const initialSecure = props.secureTextEntry;
  const [isSecureEntry, setIsSecureEntry] = useState(
    props.secureTextEntry ?? false
  );

  const [value, setValue] = useState(props.value || "");
  const [isFocused, setIsFocused] = useState(false);

  const handleValueChange = useCallback((newValue: string) => {
    if (inputType === "decimal-pad") {
      if (/^\d*\.?\d*$/.test(newValue) || newValue === "") {
        setValue(newValue);
      }
    } else {
      setValue(newValue);
    }
  }, []);

  useEffect(() => {
    handleValueChange(props.value || "");
  }, [props.value, handleValueChange]);

  const {
    leftIcon,
    rightIcon,
    hint,
    error,
    inputLabel,
    rightIconColor,
    disabled,
    onLeftIconPress,
    currency,
    height,
    rightButtonIcon,
    rightButtonIconColor,
    rightButtonDisabled,
    ignoreDisabledStyle,
    isMultilineTopPadding,
    rightButtonIconOnPress,
    showCurrency,
    leftIconColor,
    disableLeftIcon = true,
    inputType,
    renderSpaceForErrorMessage,
    barcodeButton,
    rightTextButton,
    buttonText,
    rightTextButtonOnPress,
    hideErrorIcon,
    ...restProps
  } = props;
  const [placeholder, setPlaceholder] = useState(inputLabel);
  const userCurrency = useUserCurrency();
  const accessoryViewID = `doneButton-${(
    props.inputLabel ||
    props.placeholder ||
    "input"
  )
    .replace(/\s+/g, "-")
    .toLowerCase()}`;

  const currencyLength = userCurrency?.length;
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    setPlaceholder(inputLabel);
  }, [inputLabel]);

  return (
    <Container>
      <InputContainer>
        {/*@ts-ignore*/}
        <CustomInput
          {...restProps}
          ref={props.inputRef}
          inputAccessoryViewID={accessoryViewID}
          multiline={props.multiline}
          height={height}
          secureTextEntry={isSecureEntry}
          isFocused={isFocused}
          editable={!disabled}
          disabled={disabled}
          hasValue={!!(value && value.trim().length > 0)}
          onFocus={(e: any) => {
            props.onFocus?.(e);
            setPlaceholder("");
            setIsFocused(true);
          }}
          onBlur={(e: any) => {
            props.onBlur?.(e);
            setPlaceholder(inputLabel);
            setIsFocused(false);
          }}
          barcodeButton={barcodeButton}
          hasLeftIcon={!!leftIcon}
          hasRightIcon={!!rightIcon || !!rightButtonIcon}
          placeholder={
            !isFocused || (isFocused && !value)
              ? props.searchPlaceholder
                ? props.searchPlaceholder
                : placeholder
              : ""
          }
          currency={currency}
          currencyLength={currencyLength}
          showCurrencyInFocusState={showCurrency}
          placeholderTextColor={theme["core-06"]}
          onChangeText={(text: string) => {
            handleValueChange(text);
            props.onChangeText?.(text);
          }}
          hasPredefinedValue={!!(props.value && props.value?.trim().length > 0)}
          isError={!!error}
          showRightPadding={initialSecure ?? false}
          isMultilineTopPadding={isMultilineTopPadding}
          {...(theme.isLargeScreen && {
            onMouseEnter: () => setHovered(true),
            onMouseLeave: () => setHovered(false),
          })}
          isHovered={hovered}
          style={{
            ...(placeholder && !value
              ? { ...typography["Body-R"] }
              : { ...typography["Body-M"] }),
            color:
              disabled && !ignoreDisabledStyle
                ? theme["core-04"]
                : theme["core-05"],
            ...(props?.fontFamily && {
              fontFamily: props?.fontFamily,
            }),
          }}
          selectedStyle={props.selectedStyle}
          ignoreDisabledStyle={ignoreDisabledStyle}
          {...(inputType && { keyboardType: inputType })}
          renderSpaceForErrorMessage={props.renderSpaceForErrorMessage}
        />
        <KeyboardDoneButton inputAccessoryViewID={accessoryViewID} />
        {!props.hideIconContainer && leftIcon && (
          <IconContainer position={"left"} isShownTopPadding={true}>
            <TouchableOpacity
              onPress={onLeftIconPress}
              disabled={disableLeftIcon}
            >
              <VDBIcon
                name={leftIcon}
                size={24}
                color={leftIconColor ? leftIconColor : theme["core-04"]}
                variant={IconsVariant.VDBCustom}
              />
            </TouchableOpacity>
          </IconContainer>
        )}
        {((showCurrency &&
          (isFocused || showCurrency || (value && value.trim().length > 0))) ||
          (currency && (props.value || isFocused))) && (
          <CurrencyContainer position={"left"}>
            <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
              {userCurrency}
            </VDBText>
          </CurrencyContainer>
        )}
        {rightIcon && (
          <IconContainer
            position={"right"}
            isShownTopPadding={true}
            hasError={!!error}
          >
            <TouchableOpacity onPress={props.onRightIconPress}>
              <VDBIcon
                name={rightIcon}
                size={24}
                color={rightIconColor ? rightIconColor : theme["core-04"]}
                variant={IconsVariant.VDBCustom}
              />
            </TouchableOpacity>
          </IconContainer>
        )}

        {error && !initialSecure && !hideErrorIcon && (
          <IconContainer position={"right"}>
            <VDBIcon
              name={VDBIcons.WARNING}
              size={24}
              color={theme["error-01"]}
              variant={IconsVariant.VDBCustom}
            />
          </IconContainer>
        )}
        {initialSecure && (
          <IconContainer position={"right"}>
            <TouchableOpacity onPress={() => setIsSecureEntry(!isSecureEntry)}>
              <View
                style={{
                  height: TAPPABLE_AREA,
                  width: TAPPABLE_AREA,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VDBIcon
                  name={isSecureEntry ? VDBIcons.EYE_SLASH : VDBIcons.EYE}
                  size={24}
                  color={error ? theme["error-01"] : theme["core-04"]}
                  variant={IconsVariant.VDBCustom}
                />
              </View>
            </TouchableOpacity>
          </IconContainer>
        )}

        {rightTextButton && (
          <IconContainer position={"right"}>
            <Button
              title={buttonText || ""}
              category={ButtonCategory.default}
              onPress={rightTextButtonOnPress}
              type={ButtonType.tertiary}
              disabled={rightButtonDisabled}
            />
          </IconContainer>
        )}

        {rightButtonIcon && (
          <IconContainer position={"right"}>
            <TouchableOpacity
              onPress={() => {
                rightButtonIconOnPress?.();
                handleValueChange("");
                setPlaceholder(inputLabel);
              }}
              disabled={rightButtonDisabled}
            >
              <VDBIcon
                name={rightButtonIcon}
                size={24}
                color={
                  theme[rightButtonIconColor as keyof DefaultTheme] as string
                }
                variant={IconsVariant.VDBCustom}
              />
            </TouchableOpacity>
          </IconContainer>
        )}

        {(isFocused ||
          !!(value && value?.trim().length > 0) ||
          (props.value && props.value?.trim().length > 0)) &&
          inputLabel.length !== 0 && (
            <InputLabel
              hideIconContainer={props.hideIconContainer}
              isFocused={isFocused}
              multiline={props.multiline}
              hasLeftIcon={!!leftIcon}
              pointerEvents="none"
              hasValue={!!(props.value && props.value?.trim().length > 0)}
              isHideBorder={props.isHideBorder}
              disabled={disabled}
            >
              <VDBText
                type={typography["Body-R"]}
                color={
                  disabled && !ignoreDisabledStyle
                    ? theme["core-04"]
                    : theme["core-06"]
                }
              >
                {inputLabel}
              </VDBText>
            </InputLabel>
          )}
      </InputContainer>

      {hint && (
        <>
          <Spacer size={SpaceType.Basic} />
          <VDBText>{hint}</VDBText>
        </>
      )}

      {renderSpaceForErrorMessage ? (
        <></>
      ) : (
        error && (
          <>
            <Spacer size={SpaceType.Basic} />
            <VDBText type={typography["Minor-R"]} color={theme["error-01"]}>
              {error}
            </VDBText>
          </>
        )
      )}
    </Container>
  );
};

const Container = styled.View``;
const InputContainer = styled.View``;

const CustomInput = styled.TextInput<{
  hasLeftIcon: boolean;
  hasRightIcon: boolean;
  isFocused: boolean;
  hasValue: boolean;
  isError: boolean;
  height?: string;
  multiline?: boolean;
  hasPredefinedValue?: boolean;
  disabled?: boolean;
  isHideBorder?: boolean;
  isShownTopPadding?: boolean;
  isMultilineTopPadding?: boolean;
  currency?: boolean;
  showCurrencyInFocusState?: boolean;
  currencyLength: number;
  showRightPadding: boolean;
  selectedStyle?: SelectedItemTheme;
  ignoreDisabledStyle?: boolean;
  isHovered: boolean;
  renderSpaceForErrorMessage?: boolean;
  barcodeButton?: boolean;
}>`
  width: 100%;
  height: ${({ multiline, height }: { multiline: boolean; height: string }) =>
    height || (multiline ? "auto" : "56px")};
  min-height: 56px;
  background-color: ${({
    theme,
    disabled,
    ignoreDisabledStyle,
  }: {
    theme: DefaultTheme;
    disabled: boolean;
    ignoreDisabledStyle: boolean;
  }) =>
    disabled && !ignoreDisabledStyle ? theme["core-02"] : theme["core-01"]};
  padding-left: ${({
    hasLeftIcon,
    currency,
    currencyLength,
    isShownTopPadding,
    showCurrencyInFocusState,
  }: {
    hasLeftIcon: boolean;
    currency: boolean;
    currencyLength: number;
    isShownTopPadding: boolean;
    showCurrencyInFocusState: boolean;
  }) =>
    hasLeftIcon
      ? isShownTopPadding
        ? 56
        : 48
      : currency || showCurrencyInFocusState
      ? 10 + currencyLength * 13
      : 12}px;
  padding-right: ${({
    showRightPadding,
    hasRightIcon,
    barcodeButton,
  }: {
    showRightPadding: boolean;
    hasRightIcon: boolean;
    barcodeButton: boolean;
  }) => (showRightPadding || hasRightIcon ? 50 : barcodeButton ? 40 : 12)}px;
  border-radius: ${SpaceType.Basic}px;
  border-color: ${({
    theme,
    isFocused,
    isError,
    disabled,
    selectedStyle,
    ignoreDisabledStyle,
    hasValue,
    hasPredefinedValue,
    isHovered,
  }: {
    theme: DefaultTheme;
    isFocused: boolean;
    isError: boolean;
    disabled: boolean;
    selectedStyle: SelectedItemTheme;
    ignoreDisabledStyle: boolean;
    hasValue: boolean;
    hasPredefinedValue: boolean;
    isHovered: boolean;
  }) =>
    isError
      ? theme["error-01"]
      : isFocused || isHovered
      ? (theme[
          selectedStyle
            ? (selectedStyle?.secondary as keyof DefaultTheme)
            : "main-04"
        ] as string)
      : disabled && !ignoreDisabledStyle
      ? theme["core-03"]
      : hasValue || hasPredefinedValue
      ? theme["core-06"]
      : theme["core-04"]};
  border-width: ${({ isHideBorder }: { isHideBorder: boolean }) =>
    isHideBorder ? "0px" : "1px"};
  padding-top: ${({
    isFocused,
    hasValue,
    multiline,
    hasPredefinedValue,
    isShownTopPadding,
    height,
    isMultilineTopPadding,
  }: {
    isFocused: boolean;
    hasValue: boolean;
    multiline: boolean;
    hasPredefinedValue: boolean;
    isShownTopPadding: boolean;
    height: string;
    isMultilineTopPadding: boolean;
  }) =>
    (multiline && isFocused) || (hasValue && multiline)
      ? 30
      : isFocused || multiline || hasValue || hasPredefinedValue
      ? isShownTopPadding
        ? 0
        : Platform.OS === "android" && multiline
        ? 0
        : Platform.OS === "ios" && height && !isFocused
        ? 8
        : isMultilineTopPadding
        ? 30
        : 20
      : 0}px;
  padding-right: ${({
    showRightPadding,
    hasRightIcon,
  }: {
    showRightPadding: boolean;
    hasRightIcon: boolean;
  }) => (showRightPadding || hasRightIcon ? 50 : 12)}px;
  padding-bottom: ${({
    multiline,
    height,
    isFocused,
    hasValue,
  }: {
    multiline: boolean;
    height: string;
    isFocused: boolean;
    hasValue: boolean;
  }) =>
    multiline && height && !isFocused
      ? 0
      : (multiline && isFocused) || (hasValue && multiline)
      ? 5
      : 0}px;
  outline-style: none;
`;

const IconContainer = styled.View<{
  isShownTopPadding?: boolean;
  position: "left" | "right";
  hasError?: boolean;
}>`
  position: absolute;
  justify-content: center;
  height: 100%;
  z-index: 3;
  padding-left: ${({
    position,
    isShownTopPadding,
  }: {
    position: "left" | "right";
    isShownTopPadding: boolean;
  }) => (position === "left" ? (isShownTopPadding ? 16 : 12) : 0)}px;
  padding-right: ${({
    position,
    isShownTopPadding,
    hasError,
  }: {
    position: "left" | "right";
    isShownTopPadding: boolean;
    hasError: boolean;
  }) =>
    (position === "right" ? (isShownTopPadding ? 16 : 12) : 0) +
    (hasError ? 24 : 0)}px;
  ${({ position }: { position: "left" | "right" }) =>
    position === "right" &&
    css`
      right: 0;
    `}
`;

const CurrencyContainer = styled.View<{ position: "left" | "right" }>`
  position: absolute;
  justify-content: center;
  padding-top: 20px;
  height: 100%;
  padding-left: ${({ position }: { position: "left" | "right" }) =>
    position === "left" ? 12 : 0}px;
  padding-right: ${({ position }: { position: "left" | "right" }) =>
    position === "right" ? 12 : 0}px;
  ${({ position }: { position: "left" | "right" }) =>
    position === "right" &&
    css`
      right: 0;
    `}
`;

const InputLabel = styled.View<{
  hideIconContainer?: boolean;
  isFocused?: boolean;
  multiline?: boolean;
  hasLeftIcon?: boolean;
  hasValue?: boolean;
  isHideBorder?: boolean;
  disabled?: boolean;
}>`
  position: absolute;
  z-index: 2;
  justify-content: ${({
    isFocused,
    multiline,
    hasValue,
  }: {
    isFocused: boolean;
    multiline: boolean;
    hasValue: boolean;
  }) => (isFocused || multiline || hasValue ? "flex-start" : "center")};
  height: ${({
    isFocused,
    multiline,
    hasValue,
  }: {
    isFocused: boolean;
    multiline: boolean;
    hasValue: boolean;
  }) => (isFocused || multiline || hasValue ? "auto" : "100%")};
  padding-left: ${({
    hideIconContainer,
    hasLeftIcon,
  }: {
    hideIconContainer: boolean;
    hasLeftIcon: boolean;
  }) => (hideIconContainer ? 10 : hasLeftIcon ? 46 : 10)}px;
  padding-top: ${({
    isFocused,
    multiline,
    hasValue,
  }: {
    isFocused: boolean;
    multiline: boolean;
    hasValue: boolean;
  }) => {
    // if (isHideBorder) {
    //   return 0;
    // }

    if (hasValue) {
      return 2;
    }

    return isFocused || multiline || hasValue ? 2 : 0;
  }}px;
  padding-bottom: ${({
    isFocused,
    multiline,
    hasValue,
  }: {
    isFocused: boolean;
    multiline: boolean;
    hasValue: boolean;
  }) => (isFocused || multiline || hasValue ? 6 : 20)}px;
  background-color: ${({
    theme,
    disabled,
  }: {
    theme: DefaultTheme;
    disabled: boolean;
  }) => (disabled ? "transparent" : theme["core-01"])};
  border-top-left-radius: ${SpaceType.Basic}px;
  border-top-right-radius: ${SpaceType.Basic}px;
  width: 96%;
  margin-top: ${({
    isFocused,
    multiline,
    hasValue,
  }: {
    isFocused: boolean;
    multiline: boolean;
    hasValue: boolean;
  }) => {
    // if (isHideBorder) {
    //   return 0;
    // }
    if (hasValue) {
      return 2;
    }
    return isFocused || multiline ? 2 : 0;
  }}px;
  margin-left: 2px;
`;

export default Input;
